import {
  forwardRef,
  type ReactEventHandler,
  type ReactNode,
  Ref,
  useMemo,
} from 'react';

import {
  getIntentLink,
  type IntentPayload,
  type SendIntentOptions,
  type SendIntentWithResponseOptions,
} from '@dynatrace-sdk/navigation';

import { getIntentSlots } from './item-slots.js';
import { Item } from './Item.js';
import { Prefix } from './Prefix.js';
import {
  useIntent,
  useIntentWithResponse,
  UseIntentWithResponseReturn,
} from '../../core/hooks/intent.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { AppIcon } from '../../layouts/app-header/app-icon/AppIcon.js';

/**
 * @public
 */
export interface MenuIntentBaseProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  disabled?: boolean;
  payload: IntentPayload;
  onSelect?: ReactEventHandler;
}

/**
 * @public
 */
export interface SuccessResponse {
  status: 'success';
  data: Record<string, unknown>;
}

/**
 * @public
 */
export interface ErrorResponse {
  status: 'error';
  error: Error;
}

/**
 * @public
 */
export interface MenuIntentWithResponseProps extends MenuIntentBaseProps {
  options: SendIntentWithResponseOptions<string>;
  onResponse: (response: SuccessResponse | ErrorResponse) => void;
}

/**
 * @public
 */
export interface MenuIntentWithoutResponseProps extends MenuIntentBaseProps {
  options: SendIntentOptions<IntentPayload>;
  onResponse?: never;
}

function isWithResponse(
  props: MenuIntentWithResponseProps | MenuIntentWithoutResponseProps,
): props is MenuIntentWithResponseProps {
  if ('onResponse' in props) {
    return typeof props.onResponse === 'function';
  }
  return false;
}

export type Slots = {
  icon: ReactNode | null;
  freeform: ReactNode[];
};

/**
 * @internal
 */
export function getIntentIcon(
  icon: ReactNode | undefined,
  intentIcon: string | null,
) {
  if (!icon && !intentIcon) {
    return null;
  }

  if (icon) {
    return icon;
  }

  if (intentIcon) {
    return (
      <Prefix>
        <AppIcon src={intentIcon} alt="" />
      </Prefix>
    );
  }
}

/**
 * @internal
 */
export const getHandleSelect =
  (
    intent: UseIntentWithResponseReturn,
    onSelect: ReactEventHandler | undefined,
    onResponse: (response: SuccessResponse | ErrorResponse) => void,
  ): ReactEventHandler =>
  async (e) => {
    onSelect?.(e);

    try {
      const response = await intent.send();
      onResponse(response);
    } catch {
      onResponse({
        status: 'error',
        error: new Error('Error sending intent with response'),
      });
    }
  };

const IntentWithoutResponse = /* @__PURE__ */ forwardRef<
  HTMLAnchorElement,
  MenuIntentWithoutResponseProps
>((props, ref) => {
  // discard onResponse from props
  const {
    onResponse,
    onSelect,
    payload,
    options,
    children,
    disabled,
    ...rest
  } = props;

  const intent = useIntent({
    payload,
    options,
  });

  const slots = getIntentSlots(children);

  const intentIcon = getIntentIcon(slots.prefix, intent.iconURL);

  const link = useMemo(() => {
    return getIntentLink(
      payload,
      options.recommendedAppId ?? '',
      options.recommendedIntentId ?? '',
    );
  }, [options.recommendedAppId, options.recommendedIntentId, payload]);

  return (
    <Item
      {...rest}
      as="a"
      href={disabled ? undefined : link}
      ref={ref}
      disabled={disabled}
      onSelect={(e) => {
        e.preventDefault();

        intent.send();
        onSelect?.(e);
      }}
    >
      {intentIcon}
      {slots.freeform}
    </Item>
  );
});

const IntentWithResponse = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuIntentWithResponseProps
>((props, ref) => {
  const {
    onSelect,
    onResponse,
    payload,
    options,
    children,
    disabled,
    ...rest
  } = props;

  const intent = useIntentWithResponse({
    payload,
    options,
  });

  const slots = getIntentSlots(children);

  const intentIcon = getIntentIcon(slots.prefix, intent.iconURL);

  const handleSelect = getHandleSelect(intent, onSelect, onResponse);

  return (
    <Item {...rest} ref={ref} disabled={disabled} onSelect={handleSelect}>
      {intentIcon}
      {slots.freeform}
    </Item>
  );
});

/**
 * @public
 */
export type MenuIntentProps =
  | MenuIntentWithResponseProps
  | MenuIntentWithoutResponseProps;

/**
 * A menu subcomponent to send intents.
 * @public
 */
export const Intent = /* @__PURE__ */ forwardRef<HTMLElement, MenuIntentProps>(
  (props, ref) => {
    if (isWithResponse(props)) {
      return <IntentWithResponse {...props} ref={ref as Ref<HTMLDivElement>} />;
    }
    return (
      <IntentWithoutResponse {...props} ref={ref as Ref<HTMLAnchorElement>} />
    );
  },
);

(Intent as typeof Intent & { displayName: string }).displayName = 'Menu.Intent';
